import { useEffect, useState } from 'react';
import { useTheme, Tab, Tabs } from '@mui/material';
import { PageCard } from '../../../Compounds/CardWithTitle';
import { BasicDataGrid } from '../../../Compounds/Table/BasicDataGrid';
import {
  DEFAULT_TABLE_STYLES,
  DEFAULT_ADVANCE_BUTTON_STYLE,
  DEFAULT_TAB_SX_STYLES
} from '../../../../utils/styleHelpers';
import { ComponentSpinner } from '../../../Compounds/Loading/ComponentSpinner';
import { getColumnDefsByKey } from './LinksDefs';
import {
  getBulkValidateByAssetSubject,
  bulkValidateByAssetData,
  bulkCreateByAssetField,
  getBulkValidateByAssetLoadingSubject
} from '../../../../store/Link';
import { useSnackbar } from '../../../../Context/SnackbarContext';

interface PreviewStepProps {
  nextStep: () => void;
  title: string;
  currentStep: number;
}

export interface TabProps {
  name: string;
  key: string;
}

export const PreviewStep = ({ nextStep, title, currentStep }: PreviewStepProps) => {
  const allTabs: TabProps[] = [
    { name: 'Links', key: currentStep === 2 ? 'new' : 'success' },
    { name: 'Existing', key: 'existing' },
    { name: 'Not Found', key: 'notFound' },
    { name: 'Conflicts', key: 'conflicts' }
  ];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepData, setStepData] = useState<bulkValidateByAssetData>({});
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const theme = useTheme();
  const { setSnack } = useSnackbar();

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const detectedSelectedTab = (newStepData: bulkValidateByAssetData) => {
    const indexTab = allTabs.findIndex((tab) => {
      return (newStepData[tab.key as keyof typeof newStepData] as [])?.length > 0;
    });

    return indexTab !== -1 ? indexTab : 0;
  };

  useEffect(() => {
    const dataSub = getBulkValidateByAssetSubject().subscribe((data) => {
      if (!stepData.success?.length && data.success?.length) {
        setSnack({
          message: `${data.success.length} link(s) created successfully.`,
          type: 'success',
          open: true
        });
      }
      setSelectedTab(detectedSelectedTab(data));
      setStepData(data);
    });

    const loadingSub = getBulkValidateByAssetLoadingSubject().subscribe((loading) => {
      setIsLoading(loading);
    });

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  const sxStyle = {
    ...DEFAULT_TABLE_STYLES.sx,
    maxHeight: 'calc(100vh - 320px)',
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    borderWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '.75rem',
    borderBottomRightRadius: '.75rem'
  };

  const submitStep = () => {
    bulkCreateByAssetField();
    nextStep();
  };

  const cardActions = () => (
    <div className="flex gap-3">
      {(stepData?.new?.length || 0) > 0 && currentStep === 2 && (
        <button
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={submitStep}
          data-cy="next-step-button"
          data-testid="next-step-button">
          {`Save (${stepData?.new?.length})`}
        </button>
      )}
    </div>
  );

  const selectedTabKey = allTabs[selectedTab].key;
  const rows = stepData ? (stepData[selectedTabKey as keyof typeof stepData] as []) : [];
  const columns = getColumnDefsByKey(selectedTabKey, stepData.request?.assetField || '');

  return (
    <PageCard
      dataTestId="preview-prov-assets-step"
      dataCy="preview-prov-assets-step"
      childrenWrapperClass="max-w-[60vw] min-w-full"
      title={isLoading ? 'Loading...' : title}
      cardActions={isLoading ? undefined : cardActions()}>
      <>
        {isLoading && (
          <div className="flex h-[10rem] m-auto">
            <ComponentSpinner label="Loading data..." />
          </div>
        )}
        {!isLoading && (
          <>
            <Tabs
              sx={DEFAULT_TAB_SX_STYLES}
              value={selectedTab}
              scrollButtons="auto"
              onChange={(_e, index) => {
                handleTabChange(index);
              }}
              slotProps={{
                indicator: {
                  sx: {
                    backgroundColor: theme.palette.primary.main,
                    height: 2,
                    borderRadius: 1
                  }
                }
              }}>
              {allTabs.map((tab, _index) => {
                const tabDataCount = stepData && (stepData[tab.key as keyof typeof stepData] as [])?.length;
                return (
                  <Tab
                    sx={{
                      color: 'grey9',
                      fontSize: '.8rem',
                      textTransform: 'none'
                    }}
                    key={tab.name}
                    label={`${tab.name} ${tabDataCount ? `(${tabDataCount})` : ''}`}
                  />
                );
              })}
            </Tabs>
            <BasicDataGrid
              key={selectedTab}
              rows={rows}
              columns={columns}
              sxStyle={sxStyle}
              disableColumnMenu
              showColumnVerticalBorder
              showCellVerticalBorder
              primaryKey="sfdcUpstackId"
              dataTestId="preview-grid"
            />
          </>
        )}
      </>
    </PageCard>
  );
};
